import { nanoid } from 'nanoid'
import React, { ReactNode, useCallback, useState } from 'react'
import { createContextWithHook } from '@chilipiper/utils'
import { BasicModalOptions } from '../types'
import { PromiseResolutionMethods } from './ModalFlowProvider'
import { ModalWithProvider } from './ModalWithFlowProvider'

type ShowModalData = BasicModalOptions | { modalNode: ReactNode }
type AddModalFunction = (newData: ShowModalData) => Promise<unknown>

type ModalContext = {
  addModal: AddModalFunction
}
export type ModalData = PromiseResolutionMethods &
  ShowModalData & {
    key: string
  }

const initialData: Array<ModalData> = []

const { ModalBaseProvider, ...ModalContextRest } = createContextWithHook<ModalContext>()('Modal')
export const { useModalContext } = ModalContextRest

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modals, setModals] = useState(initialData)

  const addModal = useCallback<AddModalFunction>(
    newModalData => {
      const key = nanoid()
      const setPromiseMethods = () => {
        const promise = new Promise((resolve, reject) => {
          setModals(modals =>
            modals.map(modal => (modal.key === key ? { ...modal, reject, resolve } : modal))
          )
        })
        promise.finally(setPromiseMethods)
        return promise
      }

      setModals(data => [...data, { ...newModalData, key }])
      const modalPromise = setPromiseMethods()
      return modalPromise
    },
    [setModals]
  )

  const removeModal = useCallback(
    (modalKey: string) => setModals(data => data.filter(({ key }) => key !== modalKey)),
    [setModals]
  )

  return (
    <ModalBaseProvider value={{ addModal }}>
      {children}
      {modals.map(({ key, ...modalData }) => (
        <ModalWithProvider key={key} onClose={() => removeModal(key)} {...modalData} />
      ))}
    </ModalBaseProvider>
  )
}
